import { apiFetch } from "./client";

export type EvalTargetType = "workflow" | "workflow_run" | "agent";
export type EvalStatus = "running" | "completed" | "failed";
export type EvalSeverity = "critical" | "warning" | "info";

export interface EvalFinding {
  category: "cost" | "reliability" | "instructions" | "model_choice" | "setup";
  severity: EvalSeverity;
  title: string;
  evidence: string;
  recommendation: string;
  estimatedMonthlySavingCents: number | null;
  /** "measured" is backed by telemetry; "inferred" is a judgement call. */
  confidence: "measured" | "inferred";
}

export interface EvalVerdict {
  summary: string;
  findings: EvalFinding[];
  checkedAndHealthy: string[];
  limitations: string[];
}

/** Row shape from the list endpoint — no verdict body. */
export interface EvalReportRow {
  id: string;
  targetType: EvalTargetType;
  targetId: string;
  targetName: string | null;
  status: EvalStatus;
  summary: string | null;
  headlineSeverity: string | null;
  estimatedSavingsCents: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface EvalReport extends EvalReportRow {
  verdict: EvalVerdict | null;
  coverage: { limitations?: string[] } | null;
  measurements: Record<string, unknown> | null;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  error: string | null;
}

export type ProposalStatus = "pending" | "applied" | "dismissed" | "stale" | "failed";

/** A change the Auditor drafted, awaiting a human decision. */
export interface EvalProposal {
  id: string;
  changeType: "workflow_publish" | "workflow_node_model" | "workflow_node_prompt";
  targetId: string;
  targetNodeId: string | null;
  targetName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  rationale: string | null;
  status: ProposalStatus;
  statusDetail: string | null;
  createdAt: string;
}

export interface AuditorMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  metadata?: {
    toolSteps?: Array<{ id: string; name: string; status: string; durationMs?: number }>;
    proposals?: Array<{ id: string; changeType: string; targetName: string }>;
  } | null;
  createdAt: string;
}

export const auditorChatApi = {
  getSession: (companyId: string) =>
    apiFetch<{
      session: { id: string };
      messages: AuditorMessage[];
      proposals: EvalProposal[];
    }>(`/companies/${companyId}/evals/chat/session`),

  reset: (companyId: string) =>
    apiFetch<{ session: { id: string }; messages: AuditorMessage[]; proposals: EvalProposal[] }>(
      `/companies/${companyId}/evals/chat/reset`,
      { method: "POST" },
    ),

  /** Confirm a drafted change. 409 when the target moved underneath it. */
  applyProposal: (companyId: string, proposalId: string) =>
    apiFetch<{ ok: boolean; status: string; detail?: string }>(
      `/companies/${companyId}/evals/proposals/${proposalId}/apply`,
      { method: "POST" },
    ),

  dismissProposal: (companyId: string, proposalId: string) =>
    apiFetch<{ ok: boolean }>(
      `/companies/${companyId}/evals/proposals/${proposalId}/dismiss`,
      { method: "POST" },
    ),
};

export const evalsApi = {
  /**
   * Request an evaluation. Returns as soon as the report row exists — the
   * eval itself runs in the background, so callers poll `get` until the
   * status leaves "running".
   */
  run: (
    companyId: string,
    body: { targetType: EvalTargetType; targetId: string; focus?: string },
  ) =>
    apiFetch<{ id: string; status: EvalStatus }>(`/companies/${companyId}/evals`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  list: (companyId: string, targetId?: string) =>
    apiFetch<{ reports: EvalReportRow[] }>(
      `/companies/${companyId}/evals${targetId ? `?targetId=${encodeURIComponent(targetId)}` : ""}`,
    ),

  get: (companyId: string, reportId: string) =>
    apiFetch<{ report: EvalReport }>(`/companies/${companyId}/evals/${reportId}`),
};
